import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ProjectWorkflowFoundationApi from "../../../infrastructure/api/projectWorkflowFoundation";
import { toJalaliFullDate } from "../../middleware/middleware";
import WorkflowV2SiteVisitDetailsPanel from "./WorkflowV2SiteVisitDetailsPanel";

const roleLabelFa = (role) => {
  const map = {
    Contractor: "پیمانکار",
    Supervisor: "ناظر",
    SolarentOperator: "کارشناس سولارنت",
    Management: "مدیریت",
    Customer: "متقاضی",
  };
  return map[role] || role || "—";
};

const WorkflowV2IntegrationPanel = ({
  requestId,
  source = "SolarentOperator",
  showTimeline = true,
  compact = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!requestId) return;
      setLoading(true);
      setError("");
      try {
        const summaryRes = await ProjectWorkflowFoundationApi.getWorkflowV2StatusSummary(
          source,
          requestId
        );
        if (!summaryRes.ok) {
          setError(summaryRes.message || "خطا در دریافت وضعیت WorkflowV2");
          setSummary(null);
          setTimeline([]);
        } else {
          setSummary(summaryRes.data);
        }

        if (showTimeline) {
          const timelineRes = await ProjectWorkflowFoundationApi.getWorkflowV2Timeline(
            source,
            requestId
          );
          if (timelineRes.ok) {
            setTimeline(timelineRes.data?.items || timelineRes.data?.Items || []);
          }
        }
      } catch {
        setError("خطا در دریافت وضعیت WorkflowV2");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [requestId, source, showTimeline]);

  if (!requestId) return null;

  if (loading && !summary) {
    return (
      <Box sx={{ py: 2, textAlign: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!summary?.hasWorkflowV2 && !summary?.HasWorkflowV2) {
    return null;
  }

  const stageTitle =
    summary?.currentStageTitleFa || summary?.CurrentStageTitleFa || "—";
  const nextAction =
    summary?.nextActionTitleFa || summary?.NextActionTitleFa || "—";
  const nextRole = summary?.nextActionRole || summary?.NextActionRole;
  const blocking =
    summary?.blockingReason || summary?.BlockingReason || "";
  const lastEvent =
    summary?.lastEventTitleFa || summary?.LastEventTitleFa || "—";
  const lastUpdated =
    summary?.lastUpdatedAt || summary?.LastUpdatedAt || null;
  const currentStage =
    summary?.currentWorkflowV2Stage || summary?.CurrentWorkflowV2Stage || "";
  const showVisitDetails =
    currentStage === "VisitCompleted" ||
    stageTitle === "بازدید تکمیل شد" ||
    (lastEvent && String(lastEvent).includes("بازدید"));

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, mb: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" fontWeight={700}>
            روند اجرای پروژه (WorkflowV2)
          </Typography>
          <Chip size="small" color="primary" label={stageTitle} data-testid="workflow-current-state" />
          {blocking ? (
            <Chip size="small" color="error" label="متوقف" />
          ) : null}
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Stack spacing={0.5}>
          <Typography variant="body2">
            <strong>اقدام بعدی:</strong> {nextAction}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>مسئول:</strong> {roleLabelFa(nextRole)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>آخرین رویداد:</strong> {lastEvent}
            {lastUpdated
              ? ` — ${toJalaliFullDate(lastUpdated)}`
              : ""}
          </Typography>
          {blocking ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {blocking}
            </Alert>
          ) : null}
        </Stack>

        {showVisitDetails ? (
          <WorkflowV2SiteVisitDetailsPanel requestId={requestId} source={source} />
        ) : null}

        {showTimeline && timeline.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Timeline
            </Typography>
            <Stack spacing={1} sx={{ mt: 1, maxHeight: 240, overflow: "auto" }}>
              {timeline.map((item, index) => (
                <Box
                  key={`${item.category || item.Category}-${item.relatedEntityId || item.RelatedEntityId}-${index}`}
                  sx={{
                    borderRight: 2,
                    borderColor: "primary.light",
                    pr: 1.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {item.titleFa || item.TitleFa}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(item.occurredAt || item.OccurredAt)
                      ? toJalaliFullDate(item.occurredAt || item.OccurredAt)
                      : "—"}
                    {item.actorRole || item.ActorRole
                      ? ` — ${roleLabelFa(item.actorRole || item.ActorRole)}`
                      : ""}
                  </Typography>
                  {item.description || item.Description ? (
                    <Typography variant="caption" display="block">
                      {item.description || item.Description}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
};

export default WorkflowV2IntegrationPanel;
